import { apiRequest, apiRoutes } from '../../api';
/**
 * GeoQuery.js — جستجوی مکانی هوشمند
 *
 * Fetches GeoFeatures where:
 *   - showable_in_filter_pane = false
 *   - filter_usage = 'sorting'   (we re-use the same flag for "geo query" features)
 *
 * The user picks one or more geo-features (e.g. دسترسی به پارکها),
 * sets a threshold (max distance / min score), and the component
 * calls onConditionsChange([{ featureId, threshold, featureDef }, ...])
 * so MainPage can filter visibleProperties accordingly.
 *
 * Additionally it loads related POIs (from poi_types on the feature)
 * and calls onPOIsChange(poisArray) so MapContainer can show them.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Add as AddIcon, Close as CloseIcon, MyLocation as MyLocationIcon } from '@mui/icons-material';
import './GeoQuery.css';

// Ratio to approximate network distance from Euclidean (to be replaced with real routing later)
const NETWORK_DISTANCE_FACTOR = 0.6;

export default function GeoQuery({
    onConditionsChange,
    onPOIsChange,
    onFlashBuffers,
    setShowLayerControl,
    setShowSearch,
}) {
    const [open, setOpen] = useState(false);
    const [features, setFeatures] = useState([]);       // available geo-features
    const [loading, setLoading] = useState(false);
    const [conditions, setConditions] = useState([]);    // [{ featureId, threshold, featureDef, pois, poisLoading }]
    const [addingId, setAddingId] = useState(null);      // featureId being added
    const sliderTimers = useRef({});                     // debounce timers per featureId

    // ── fetch available geo-features ────────────────────────────────────
    useEffect(() => {
        if (!open || features.length) return;
        setLoading(true);
        apiRequest(apiRoutes.geo.features, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            params: { showable_in_filter_pane: false, filter_usage: 'filtering' },
        })
            .then(r => r.ok ? r.json() : Promise.reject(r))
            .then(data => {
                const list = (Array.isArray(data) ? data : data.results || [])
                    .filter(f => f.is_active !== false)
                    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
                setFeatures(list);
            })
            .catch(err => undefined)
            .finally(() => setLoading(false));
    }, [open]); // eslint-disable-line

    // ── notify parent whenever conditions change ─────────────────────────
    useEffect(() => {
        if (onConditionsChange) onConditionsChange(conditions);
    }, [conditions, onConditionsChange]);

    // ── close other panels ───────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            if (setShowLayerControl) setShowLayerControl(false);
            if (setShowSearch) setShowSearch(false);
        }
    }, [open, setShowLayerControl, setShowSearch]);

    // ── load POIs for a feature ──────────────────────────────────────────
    const loadPOIsForFeature = useCallback(async (featureId, poiTypeIds) => {
        if (!poiTypeIds?.length) return [];
        const allPois = [];
        for (const typeId of poiTypeIds) {
            try {
                const r = await apiRequest(apiRoutes.poi.data, { params: { poi_type_id: typeId } });
                if (!r.ok) continue;
                const data = await r.json();
                const items = data.pois?.features || data.pois || [];
                allPois.push(...items);
            } catch { /* skip */ }
        }
        return allPois;
    }, []);

    // aggregate all POIs across active conditions and notify map
    const refreshMapPOIs = useCallback((updatedConditions) => {
        const all = updatedConditions.flatMap(c => c.pois || []);
        if (onPOIsChange) onPOIsChange(all);
    }, [onPOIsChange]);

    // ── add a condition ──────────────────────────────────────────────────
    const addCondition = useCallback(async (feature) => {
        if (conditions.some(c => c.featureId === feature.id)) return;
        setAddingId(feature.id);

        const newCond = {
            featureId: feature.id,
            featureDef: feature,
            // default threshold: midpoint of range, or 50
            threshold: feature.min_value != null && feature.max_value != null
                ? Math.round((parseFloat(feature.min_value) + parseFloat(feature.max_value)) / 2)
                : 50,
            pois: [],
            poisLoading: true,
        };

        const next = [...conditions, newCond];
        setConditions(next);

        // load POIs in background
        const pois = await loadPOIsForFeature(feature.id, feature.poi_type_ids);
        setConditions(prev => {
            const updated = prev.map(c =>
                c.featureId === feature.id ? { ...c, pois, poisLoading: false } : c
            );
            refreshMapPOIs(updated);
            // flash buffers for the newly added condition only
            if (onFlashBuffers) {
                const newCond = updated.find(c => c.featureId === feature.id);
                if (newCond) onFlashBuffers([newCond]);
            }
            return updated;
        });
        setAddingId(null);
    }, [conditions, loadPOIsForFeature, refreshMapPOIs]);

    // ── remove a condition ───────────────────────────────────────────────
    const removeCondition = useCallback((featureId) => {
        setConditions(prev => {
            const updated = prev.filter(c => c.featureId !== featureId);
            refreshMapPOIs(updated);
            return updated;
        });
    }, [refreshMapPOIs]);

    // ── update threshold for a condition ────────────────────────────────
    const updateThreshold = useCallback((featureId, value) => {
        setConditions(prev => {
            const updated = prev.map(c =>
                c.featureId === featureId ? { ...c, threshold: value } : c
            );
            // debounce buffer flash on slider drag
            if (onFlashBuffers) {
                if (sliderTimers.current[featureId]) clearTimeout(sliderTimers.current[featureId]);
                sliderTimers.current[featureId] = setTimeout(() => {
                    const cond = updated.find(c => c.featureId === featureId);
                    if (cond) onFlashBuffers([cond]);
                }, 400);
            }
            return updated;
        });
    }, [onFlashBuffers]);

    const activeCount = conditions.length;

    return (
        <>
            {/* ── Toolbar button ── */}
            <button
                className={`mc-tool-btn geoquery-toggle ${open ? 'active' : ''}`}
                onClick={() => setOpen(v => !v)}
                title="جستجوی مکانی هوشمند"
                aria-pressed={open}
                aria-label="جستجوی مکانی هوشمند"
            >
                <MyLocationIcon fontSize="small" />
                {activeCount > 0 && (
                    <span className="mc-tool-badge">{activeCount}</span>
                )}
            </button>

            {/* ── Panel ── */}
            {open && (
                <div className="gq-panel" dir="rtl">
                    <div className="gq-header">
                        <span className="gq-title">🔍 جستجوی مکانی هوشمند</span>
                        <button className="gq-close" onClick={() => setOpen(false)} aria-label="بستن">
                            <CloseIcon fontSize="small" />
                        </button>
                    </div>

                    {/* ── Active conditions ── */}
                    {conditions.length > 0 && (
                        <div className="gq-conditions">
                            {conditions.map(cond => {
                                const def = cond.featureDef;
                                const min = def.min_value ?? 0;
                                const max = def.max_value ?? 100;
                                const isScore = def.feature_type === 'score';
                                const label = isScore ? 'حداقل امتیاز' : 'حداکثر فاصله (متر)';
                                return (
                                    <div key={cond.featureId} className="gq-cond-card">
                                        <div className="gq-cond-header">
                                            <span className="gq-cond-name">{def.description || def.name}</span>
                                            <button
                                                className="gq-cond-remove"
                                                onClick={() => removeCondition(cond.featureId)}
                                                aria-label="حذف شرط"
                                            >
                                                <CloseIcon style={{ fontSize: 14 }} />
                                            </button>
                                        </div>

                                        <div className="gq-cond-body">
                                            <div className="gq-cond-label">
                                                {label}: <strong>{cond.threshold}</strong>
                                            </div>
                                            <input
                                                type="range"
                                                className="gq-slider"
                                                min={min}
                                                max={max}
                                                step={isScore ? 1 : 50}
                                                value={cond.threshold}
                                                onChange={e => updateThreshold(cond.featureId, parseFloat(e.target.value))}
                                                aria-label={label}
                                            />
                                            <div className="gq-cond-range">
                                                <span>{min}</span>
                                                <span>{max}</span>
                                            </div>
                                        </div>

                                        {/* POI status */}
                                        {cond.poisLoading && (
                                            <div className="gq-poi-status loading">⏳ در حال بارگذاری POI‌ها...</div>
                                        )}
                                        {!cond.poisLoading && cond.pois.length > 0 && (
                                            <div className="gq-poi-status ok">
                                                📍 {cond.pois.length} مکان مرتبط روی نقشه
                                            </div>
                                        )}
                                        {!cond.poisLoading && def.poi_type_ids?.length > 0 && cond.pois.length === 0 && (
                                            <div className="gq-poi-status empty">
                                                ⚠️ مکانی یافت نشد
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* ── Feature picker ── */}
                    <div className="gq-picker-label">
                        {conditions.length === 0
                            ? 'یک ویژگی مکانی انتخاب کنید:'
                            : '+ افزودن شرط دیگر:'}
                    </div>

                    {loading && (
                        <div className="gq-loading">در حال بارگذاری ویژگی‌ها...</div>
                    )}

                    {!loading && features.length === 0 && (
                        <div className="gq-empty">ویژگی مکانی فعالی تعریف نشده است.</div>
                    )}

                    <div className="gq-feature-grid">
                        {features
                            .filter(f => !conditions.some(c => c.featureId === f.id))
                            .map(f => (
                                <button
                                    key={f.id}
                                    className={`gq-feature-chip ${addingId === f.id ? 'loading' : ''}`}
                                    onClick={() => addCondition(f)}
                                    disabled={!!addingId}
                                    title={f.description || f.name}
                                >
                                    <AddIcon style={{ fontSize: 12 }} />
                                    <span>{f.description || f.name}</span>
                                </button>
                            ))}
                    </div>

                    {/* ── Clear all ── */}
                    {conditions.length > 0 && (
                        <button
                            className="gq-clear-all"
                            onClick={() => {
                                setConditions([]);
                                if (onPOIsChange) onPOIsChange([]);
                            }}
                        >
                            پاک کردن همه شرط‌ها
                        </button>
                    )}
                </div>
            )}
        </>
    );
}
